import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';

import useTranslations from '@/i18n/useTranslations';
import { ensureArtworkQr, getArtworkQr } from '@/services/artworkQr.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import {
  buildArtworkQrFilename,
  downloadQrPng,
  normalizeArtworkQrPayload,
} from './utils';

import './index.css';

export default function QrPreview({ open, close, artworkData }) {
  const t = useTranslations('common.shared.artwork_view.options.qr_code');
  const [qrData, setQrData] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState('');
  const qrRef = useRef(null);

  const artworkId = artworkData?._id ?? null;
  const artworkTitle = artworkData?.artwork_about?.artwork_title || 'Artwork';
  const hasQr = Boolean(qrData?.qrUrl);

  useEffect(() => {
    if (!open) {
      setError('');
      setCheckingExisting(false);
      setLoading(false);
      setDownloading(null);
      return;
    }

    let ignore = false;
    const existingQr = normalizeArtworkQrPayload(artworkData);
    if (existingQr) {
      setQrData(existingQr);
      setCheckingExisting(false);
      return () => {
        ignore = true;
      };
    }

    if (!artworkId) {
      setQrData(null);
      setCheckingExisting(false);
      return () => {
        ignore = true;
      };
    }

    setCheckingExisting(true);
    setError('');
    getArtworkQr(artworkId)
      .then((loadedQr) => {
        if (ignore) {
          return;
        }
        setQrData(loadedQr);
      })
      .catch((loadError) => {
        if (ignore) {
          return;
        }
        setError(loadError?.message || t('errors.load'));
        setQrData(null);
      })
      .finally(() => {
        if (!ignore) {
          setCheckingExisting(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [open, artworkId, artworkData, t]);

  const qrSvgElement = () => qrRef.current?.querySelector('svg') ?? null;

  const displayUrl = useMemo(() => qrData?.qrUrl || '', [qrData]);

  async function handleGenerate() {
    if (!artworkId || loading) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      const nextQrData = await ensureArtworkQr(artworkId);
      setQrData(nextQrData);
    } catch (generateError) {
      setError(generateError?.message || t('errors.generate'));
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPng() {
    const svg = qrSvgElement();
    if (!svg || downloading) {
      return;
    }

    setDownloading('png');
    setError('');
    try {
      await downloadQrPng(svg, buildArtworkQrFilename(artworkData, 'png'));
    } catch (downloadError) {
      setError(downloadError?.message || t('errors.download'));
    } finally {
      setDownloading(null);
    }
  }

  if (!artworkData) {
    return null;
  }

  return (
    <GenericPopup
      open={open}
      className="artwork-qr-preview"
      opacity
      onBackgroundClick={close}
      onTouchStart={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
      onWheel={e => e.stopPropagation()}
    >
      <div className="artwork-qr-preview__header">
        <Text className="artwork-qr-preview__title">{t('title')}</Text>
        <XIcon className="artwork-qr-preview__close" onClick={close} />
      </div>

      {checkingExisting ? (
        <div className="artwork-qr-preview__intro">
          <Text className="artwork-qr-preview__artwork-title">{artworkTitle}</Text>
          <ContinueButton
            className="artwork-qr-preview__primary"
            loading
            turnOff
          >
            {t('checking')}
          </ContinueButton>
        </div>
      ) : !hasQr ? (
        <div className="artwork-qr-preview__intro">
          <Text className="artwork-qr-preview__artwork-title">{artworkTitle}</Text>
          <Text className="artwork-qr-preview__copy" paragraph>
            {t('intro')}
          </Text>
          <ContinueButton
            className="artwork-qr-preview__primary"
            loading={loading}
            onClick={handleGenerate}
          >
            {t('generate')}
          </ContinueButton>
        </div>
      ) : (
        <div className="artwork-qr-preview__result">
          <div className="artwork-qr-preview__qr-frame" ref={qrRef}>
            <QRCode
              className="artwork-qr-preview__qr"
              value={displayUrl}
              size={240}
              level="M"
            />
          </div>

          <div className="artwork-qr-preview__meta">
            <Text className="artwork-qr-preview__artwork-title">{artworkTitle}</Text>
            {qrData?.isActive === false && (
              <Text className="artwork-qr-preview__warning" paragraph>
                {t('inactive_warning')}
              </Text>
            )}
            <Text className="artwork-qr-preview__url" paragraph>
              {displayUrl}
            </Text>
          </div>

          <div className="artwork-qr-preview__actions">
            <ContinueButton
              className="artwork-qr-preview__primary"
              loading={downloading === 'png'}
              onClick={handleDownloadPng}
            >
              {t('download_png')}
            </ContinueButton>
          </div>
        </div>
      )}

      {error && (
        <Text className="artwork-qr-preview__error" paragraph>
          {error}
        </Text>
      )}
    </GenericPopup>
  );
}
